import classNames from 'classnames';
import { NSCard, NSNavTabs } from 'bricks';
import RenderIf from 'components/RenderIf/RenderIf';
import { PRO_FORMA_TABS, PRO_FORMA_TABLE_NAV } from 'views/ProFormaTable/constants';
import { faWarning } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ProFormaQuickStartValues } from 'views/public/ProFormaQuickStart/types';
import { ISignUpManager } from 'views/public/SignUp/useSignUpManager';
import { BOEScenario } from 'views/DealDashboard/BOE/types';
import { isLandDevelopmentModel } from 'helpers/proForma';
import { isEmpty } from 'lodash';
import ProFormaDevelopment from './ProFormaDevelopment/ProFormaDevelopment';
import ProFormaGeneralInformation from './ProFormaGeneralInformation/ProFormaGeneralInformation';
import ProFormaOperations from './ProFormaOperations/ProFormaOperations';
import ProFormaDisposition from './ProFormaDisposition/ProFormaDisposition';
import { useProFormaSocketContext } from './socketContext/ProFormaSocketProvider';
import { checkUsesSourcesUnequal, getBOEProFormaDiscrepancies } from '../../helpers';
import './ProForma.scss';
import SignupOverlay from './SignupOverlay/SignupOverlay';
import DevelopmentTimelineBar from './DevelopmentTimelineBar/DevelopmentTimelineBar';

interface IProFormaBaseProps {
    navigateToSection: (sideNavName: string, tabName?: string) => void;
    currentSection?: string;
    currentTab?: string;
    hasWriteAccessToProForma: boolean;
    proFormaSignUpData?: ProFormaQuickStartValues;
    setProFormaSignUpData?: (data: ProFormaQuickStartValues) => void;
    boeScenario?: BOEScenario | undefined;
}
interface IProFormaSignUpProps extends IProFormaBaseProps {
    signUpMode: true;
    displayManualSignUp: boolean;
    setDisplayManualSignUp: (display: boolean) => void;
    signUpManager: ISignUpManager;
}
interface IProFormaNonSignUpProps extends IProFormaBaseProps {
    signUpMode?: false;
    displayManualSignUp?: boolean;
    setDisplayManualSignUp?: (display: boolean) => void;
    signUpManager?: ISignUpManager;
}

export type IProFormaProps = IProFormaSignUpProps | IProFormaNonSignUpProps;

const ProForma = ({
    navigateToSection,
    currentSection,
    currentTab,
    hasWriteAccessToProForma,
    signUpMode = false,
    proFormaSignUpData,
    setProFormaSignUpData,
    displayManualSignUp,
    setDisplayManualSignUp,
    signUpManager,
    boeScenario,
}: IProFormaProps) => {
    const {
        proForma, uses, sources, incomeSummary, expenses,
        operatingInfo, shouldUseProjectActuals,
    } = useProFormaSocketContext();

    const isLandDevelopment = isLandDevelopmentModel(proForma?.proFormaModelId);

    const tabEntries = Object.entries(PRO_FORMA_TABS).filter(
        ([key]) => !(isLandDevelopment && key === 'OPERATIONS'),
    );

    const boeDiscrepancies = boeScenario ? getBOEProFormaDiscrepancies(
        proForma, incomeSummary, expenses, operatingInfo, boeScenario,
    ) : undefined;

    const getProFormaTabHeapClassName = (tabName: string) => ({
        [PRO_FORMA_TABS.GENERAL_INFORMATION.name]: 'heap-deal-pro-forma-general-information',
        [PRO_FORMA_TABS.DEVELOPMENT.name]: 'heap-deal-pro-forma-development',
        [PRO_FORMA_TABS.OPERATIONS.name]: 'heap-deal-pro-forma-operations',
        [PRO_FORMA_TABS.DISPOSITION.name]: 'heap-deal-pro-forma-disposition',
    }[tabName] || '');

    return (
        <>
            <RenderIf isLoading={isEmpty(proForma)} isTrue={!isEmpty(proForma)}>
                <DevelopmentTimelineBar navigateToSection={navigateToSection} />
                <NSNavTabs
                    containerClassName="px-0"
                    navItems={tabEntries.map(([, { prefix, name }], index) => ({
                        callback: e => {
                            e.preventDefault();
                            navigateToSection(currentSection || PRO_FORMA_TABLE_NAV.PRO_FORMA.name, name);
                        },
                        navPrefix: prefix,
                        className: classNames('text-nowrap', {
                            'mr-1': index !== tabEntries.length - 1,
                        }, getProFormaTabHeapClassName(name)),
                        name,
                        icon: name === PRO_FORMA_TABS.DEVELOPMENT.name
                        && checkUsesSourcesUnequal(uses, sources)
                        && !shouldUseProjectActuals
                            ? (
                                <div className="ProForma__warning-icon d-flex align-items-center justify-content-center">
                                    <FontAwesomeIcon icon={faWarning} className="text-warning" />
                                </div>
                            )
                            : undefined,
                        isActive: currentTab === name,
                        id: `proforma-tab-${index}`,
                    }))}
                />
                <hr className="my-0" />
                <NSCard className="p-3">
                    <RenderIf
                        isTrue={currentTab === PRO_FORMA_TABS.GENERAL_INFORMATION.name}
                    >
                        <ProFormaGeneralInformation
                            hasWriteAccessToProForma={hasWriteAccessToProForma}
                            signUpMode={signUpMode}
                            proFormaSignUpData={proFormaSignUpData}
                            setProFormaSignUpData={setProFormaSignUpData}
                            boeDiscrepancies={boeDiscrepancies ? boeDiscrepancies[PRO_FORMA_TABS.GENERAL_INFORMATION.name] : undefined}
                        />
                    </RenderIf>
                    <RenderIf
                        isTrue={currentTab === PRO_FORMA_TABS.DEVELOPMENT.name}
                    >
                        <ProFormaDevelopment
                            navigateToSection={navigateToSection}
                            hasWriteAccessToProForma={hasWriteAccessToProForma}
                            signUpMode={signUpMode}
                            boeDiscrepancies={boeDiscrepancies ? boeDiscrepancies[PRO_FORMA_TABS.DEVELOPMENT.name] : undefined}
                        />
                    </RenderIf>
                    <RenderIf
                        isTrue={currentTab === PRO_FORMA_TABS.OPERATIONS.name && !isLandDevelopment}
                    >
                        <ProFormaOperations
                            hasWriteAccessToProForma={hasWriteAccessToProForma}
                            signUpMode={signUpMode}
                            navigateToSection={navigateToSection}
                            boeDiscrepancies={boeDiscrepancies ? boeDiscrepancies[PRO_FORMA_TABS.OPERATIONS.name] : undefined}
                        />
                    </RenderIf>
                    <RenderIf
                        isTrue={currentTab === PRO_FORMA_TABS.DISPOSITION.name}
                    >
                        <ProFormaDisposition
                            hasWriteAccessToProForma={hasWriteAccessToProForma}
                            proFormaSignUpData={proFormaSignUpData}
                            boeDiscrepancies={boeDiscrepancies ? boeDiscrepancies[PRO_FORMA_TABS.DISPOSITION.name] : undefined}
                        />
                    </RenderIf>
                    {
                        signUpMode &&
                        currentTab !== PRO_FORMA_TABS.GENERAL_INFORMATION.name &&
                        signUpManager && (
                            <SignupOverlay
                                displayManualSignUp={displayManualSignUp!}
                                setDisplayManualSignUp={setDisplayManualSignUp!}
                                signUpManager={signUpManager}
                            />
                        )
                    }
                </NSCard>
            </RenderIf>
        </>
    );
};

export default ProForma;
